import { Box, Stack, TextField, ThemeProvider, Typography, useMediaQuery } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusCard } from "@smartrr/shared/customerPortal/components/AquariusCard";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { Check } from "akar-icons";
import React, { useEffect, useRef, useState } from "react";
import OtpInput from "react-otp-input";

import { typedCustomerFrontendApi } from "@customer-app/shared/typedCustomerFrontendApi";

import { AlertIcon } from "../../libs/components/AlertIcon";
import { AuthErrorMessage } from "../../libs/components/AuthErrorMessage";
import { SuccessCheckIcon } from "../../libs/components/SuccessCheckIcon";
import {
  AuthFlowContainerStyles,
  HelpErrorTextStyles,
  LoginButton,
  OTPContainer,
  OtpInputErrorStyles,
  OtpInputFocusStyles,
  OtpInputStyles,
  emailTextFieldStyles,
  footerTypographyLinkWrapperStyles,
  footerTypographyStyles,
} from "../../libs/styles";
import { ErrorType } from "../../libs/types";
import { getOTPButtonErrorText, validateEmailAddressFormat } from "../../libs/utils";

import "../../../account/fonts.css";
import { useThemeOverrides } from "../../libs/hooks/useThemeOverrides";

/**
 * @todo: revisit these types _ they are global declarations which effect the entire app
 */
declare module "@mui/material/styles" {
  interface TypographyVariants {
    caption1: React.CSSProperties;
    caption2: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    caption1?: React.CSSProperties;
    caption2?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    //Not supporting default `caption` variant, replacing it with caption1 & caption2 for more flexibility
    caption1: true;
    caption2: true;
    // Unsupported ---
    subtitle1: false;
    subtitle2: false;
    caption: false;
    overline: false;
  }
}

export default function OTPLoginFlow({ shopifyDomain }: { shopifyDomain: string | null }): JSX.Element {
  const theme = useThemeOverrides();

  const isMobile = useMediaQuery("(max-width:600px)");

  const [verificationKey, setVerificationKey] = useState<{
    checkString: string;
    appCheck: string;
  } | null>(null);

  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [otp, setOtp] = useState<string | undefined>();

  const [isLoading, setLoading] = useState(false);

  const [errorType, setErrorType] = useState<ErrorType | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const [showErrorOnEmail, setShowErrorOnEmail] = useState(false);
  const [showErrorOnOtp, setShowErrorOnOtp] = useState(false);

  const [userPassedOTP, setUserPassedOTP] = useState(false);
  const [userSubmittedOTP, setUserSubmittedOTP] = useState(false);

  const [step, setStep] = useState<"login" | "otp">("login");

  const sendUserOTP = (e: any) => {
    setLoading(true);
    if (e) {
      e.preventDefault();
    }

    if (!email) {
      setErrorType("no_email");
      setErrorMessage("Please enter email address.");
      return;
    }

    const formattedEmail = email.trim().toLocaleLowerCase();

    if (!validateEmailAddressFormat(formattedEmail)) {
      setErrorType("wrong_email");
      setErrorMessage("Please enter valid email address.");
      return;
    }

    typedCustomerFrontendApi
      .postReq("/auth/otp", {
        reqBody: {
          email: formattedEmail,
          orgShopifyDomain: shopifyDomain,
          reqType: "login",
        },
      })
      .then(res => {
        if (res.type === "success") {
          const { verificationKey } = res.body;

          const decodedVerificationKey = JSON.parse(atob(verificationKey));
          setErrorType(undefined);
          setErrorMessage(undefined);
          setStep("otp");
          setVerificationKey(decodedVerificationKey);
          setOtp(undefined);
        } else {
          switch (true) {
            // User hasn't activated account through activation link
            case res.status === 401: {
              setErrorType("not_enabled");
              break;
            }
            //Too many requests. Please try again in 10 minutes.
            case res.status === 403: {
              setErrorType("too_many_email");
              break;
            }
            //Couldn't find customer for: {org}
            case res.status === 404: {
              setErrorType("wrong_email");
              break;
            }
            // accountStatus isn't pulled from backend so relying on status codes instead
            case res.status === 405: {
              setErrorType("account_locked");
              break;
            }
            // User hasn't activated account through activation link and email couldn't be sent
            case res.status === 406: {
              setErrorType("cannot_send_invite");
              break;
            }
            //Internal server error
            default: {
              setErrorType("server_error");
              break;
            }
          }
          setErrorMessage(res.message);
        }
        setShowErrorOnEmail(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const submitBtnRef = useRef<HTMLButtonElement | null>(null);

  const authenticateUsingOTP = () => {
    setLoading(true);
    typedCustomerFrontendApi
      .postReq("/auth/otp/verify", {
        reqBody: {
          email,
          otp,
          verificationKey,
        },
      })
      .then(res => {
        if (res.type === "success") {
          if (res.body.otpStatus === "SUCCESS") {
            setUserPassedOTP(true);
            setErrorType(undefined);
            setErrorMessage(undefined);
            setPassword(atob(res.body.confirmationHash));
            setTimeout(() => {
              if (submitBtnRef.current) {
                submitBtnRef.current.click();
              }
            }, 200);
            return;
          }
        } else {
          switch (true) {
            case res.message.includes("Account temporarily locked"): {
              setErrorType("account_locked");
              break;
            }
            case res.message.includes("Too many"): {
              setErrorType("too_many_otp");
              break;
            }
            case res.message === "Invalid OTP code.": {
              setErrorType("wrong_otp");
              break;
            }
            case res.message === "Code already used.": {
              setErrorType("code_used");
              break;
            }
            case res.message.includes("Code has expired"): {
              setErrorType("code_expired");
              break;
            }
            default: {
              setErrorType("server_error");
            }
          }
          setErrorMessage(res.message);
        }
        setShowErrorOnOtp(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleEmailInputChange = (email: string) => {
    setEmail(email.toLocaleLowerCase());
    if (checkIfEmailError(errorType)) {
      setErrorType(undefined);
      setErrorMessage(undefined);
    }
  };

  const checkIfEmailError = (errorType: ErrorType | undefined) => {
    if (!errorType) {
      return false;
    }
    return ["too_many_email", "wrong_email", "no_email", "not_enabled", "cannot_send_invite"].includes(errorType);
  };

  const handleOTPInputChange = (value: string) => {
    setOtp(value.toLocaleUpperCase());
    if (checkIfOTPError(errorType)) {
      setErrorMessage(undefined);
      setErrorType(undefined);
    }
  };

  const checkIfOTPError = (errorType: ErrorType | undefined) => {
    if (!errorType) {
      return false;
    }
    return ["too_many_otp", "wrong_otp", "account_locked", "code_used", "code_expired"].includes(errorType);
  };

  const renderOTPButtonText = () => {
    if (userPassedOTP) {
      return <Check size={22} color={theme.modernIconsColor} />;
    }
    if (isLoading) {
      return <DotSpinner size={22} color={theme.modernIconsColor} />;
    }
    if (errorType && checkIfOTPError(errorType)) {
      return <span>{getOTPButtonErrorText(errorType)}</span>;
    }
    return (
      <span>
        {typedLocalize("passwordlessLogin.remainingCharacters", {
          num: 6 - (otp?.length || 0),
        })}
      </span>
    );
  };

  const renderOTPHelperErrorText = () => {
    if (errorType === "too_many_otp" || errorType === "account_locked") {
      return (
        <AuthErrorMessage
          errorMessage={errorMessage}
          localizedText={typedLocalize("passwordlessLogin.tryAgain")}
          showTimer
        />
      );
    }

    if (userSubmittedOTP) {
      return <AuthErrorMessage localizedText={typedLocalize("passwordlessLogin.newCodeSent")} />;
    }

    return (
      <Box
        onClick={() => {
          setUserSubmittedOTP(true);
          sendUserOTP(null);
        }}
        sx={HelpErrorTextStyles}
      >
        {typedLocalize("passwordlessLogin.resendCode")}
      </Box>
    );
  };

  const renderNoAccountTextBasedOnError = () => {
    if (!errorMessage) {
      return typedLocalize("passwordlessLogin.noAccount");
    }
    switch (errorType) {
      case "wrong_email": {
        return typedLocalize("passwordlessLogin.noAccountFound");
      }
      case "no_email": {
        return typedLocalize("passwordlessLogin.noEmail");
      }
      case "server_error": {
        console.error(errorMessage);
        return typedLocalize("passwordlessLogin.serverErrorFallback");
      }
      case "too_many_email": {
        return (
          <AuthErrorMessage
            errorMessage={errorMessage}
            localizedText={typedLocalize("passwordlessLogin.tryAgain")}
            showTimer
          />
        );
      }
      case "account_locked": {
        return (
          <AuthErrorMessage
            errorMessage={errorMessage}
            localizedText={typedLocalize("passwordlessLogin.tryAgain")}
            showTimer
          />
        );
      }
      case "not_enabled": {
        return <AuthErrorMessage localizedText={typedLocalize("passwordlessLogin.notActivatedError")} />;
      }
      case "cannot_send_invite": {
        return (
          <AuthErrorMessage localizedText={typedLocalize("passwordlessLogin.notActivatedFailedInviteError")} />
        );
      }
      default: {
        return typedLocalize("passwordlessLogin.noAccount");
      }
    }
  };

  /**
   * `otp` value updates on otp input change
   * this use effect watches for changes on `otp` and when its length is 6 it will
   * automatically submit otp
   */
  useEffect(() => {
    if (otp?.length === 6) {
      setLoading(true);
      authenticateUsingOTP();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  useEffect(() => {
    setTimeout(() => {
      if (showErrorOnOtp) {
        setShowErrorOnOtp(false);
      }
      if (showErrorOnEmail) {
        setShowErrorOnEmail(false);
      }
    }, 1000);
  }, [showErrorOnOtp, showErrorOnEmail]);

  const getComponentDependingOnStep = (step: string) => {
    switch (step) {
      case "login": {
        return (
          <React.Fragment>
            <Typography variant="h2" className={prefixClassName("otp-header")}>
              {typedLocalize("passwordlessLogin.greeting")}
            </Typography>
            <form onSubmit={sendUserOTP} style={{ width: "100%" }}>
              <Stack gap="30px" sx={{ alignItems: "center" }}>
                <TextField
                  autoComplete="email"
                  type="email"
                  className={prefixClassName("otp-input")}
                  placeholder={typedLocalize("passwordlessLogin.emailPlaceholder")}
                  InputProps={{
                    endAdornment:
                      errorType === "wrong_email" ? <AlertIcon /> : email ? <SuccessCheckIcon /> : undefined,
                  }}
                  value={email}
                  onChange={e => handleEmailInputChange(e.target.value)}
                  sx={emailTextFieldStyles(theme, errorType)}
                />
                <LoginButton
                  theme={theme}
                  className={prefixClassName("otp-submit-button")}
                  type="submit"
                  variant="contained"
                  onClick={sendUserOTP}
                  disabled={isLoading}
                >
                  {typedLocalize("passwordlessLogin.loginBtn")}
                </LoginButton>
              </Stack>
            </form>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              sx={{ width: "100%", fontSize: "12px" }}
            >
              <Typography
                className={prefixClassName("otp-create-account-wrapper")}
                variant="caption1"
                sx={footerTypographyStyles(checkIfEmailError, errorType)}
              >
                {renderNoAccountTextBasedOnError()}{" "}
                {(!errorType || errorType === "wrong_email") && (
                  <Typography variant="caption1" sx={footerTypographyLinkWrapperStyles(errorType)}>
                    <span
                      className={prefixClassName("otp-create-account-text")}
                      onClick={() => {
                        return window.location.replace(`https://${shopifyDomain}/account/register`);
                      }}
                    >
                      {typedLocalize("passwordlessLogin.createOne", {
                        ending: errorType === "wrong_email" ? "?" : ".",
                      })}
                    </span>
                  </Typography>
                )}
              </Typography>
            </Stack>
          </React.Fragment>
        );
      }
      case "otp": {
        return (
          <React.Fragment>
            {/* auth header */}
            <Typography variant="h2" className={prefixClassName("otp-header")}>
              {typedLocalize("passwordlessLogin.enterCode")}
            </Typography>
            {/* auth body */}
            <OTPContainer shake={checkIfOTPError(errorType) && showErrorOnOtp} isMobile={isMobile}>
              <OtpInput
                className={"smartrr-otp-input-characters"}
                shouldAutoFocus
                value={otp}
                onChange={handleOTPInputChange} // we dont submit with button, but check otp on change and automatically submit
                numInputs={6}
                hasErrored={checkIfOTPError(errorType)}
                errorStyle={OtpInputErrorStyles()}
                inputStyle={OtpInputStyles(userPassedOTP, isMobile)}
                focusStyle={OtpInputFocusStyles(userPassedOTP, isMobile)}
              />
              {/* Hidden input leveraged to for handling OTP*/}
              <div style={{ display: "none" }}>
                <input
                  type="email"
                  value={email ?? ""}
                  name="customer[email]"
                  placeholder="{{ 'customer.login_page.email' | t }}"
                />
                <input
                  type="password"
                  value={password ?? ""}
                  name="customer[password]"
                  placeholder="{{ 'customer.login_page.password' | t }}"
                />
              </div>
            </OTPContainer>
            <LoginButton
              theme={theme}
              ref={submitBtnRef}
              className={prefixClassName("otp-submit-button")}
              type="submit"
              variant="contained"
              disabled={!userPassedOTP}
            >
              {renderOTPButtonText()}
            </LoginButton>
            {renderOTPHelperErrorText()}
          </React.Fragment>
        );
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <AquariusCard
        data-testid="otp-wrapper"
        className={prefixClassName("otp-wrapper")}
        sx={AuthFlowContainerStyles}
      >
        {getComponentDependingOnStep(step)}
      </AquariusCard>
    </ThemeProvider>
  );
}
